import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  validateAllocation,
  validatePurchaseHeader,
  validatePurchaseLine,
  type Purchase,
  type PurchaseAllocation,
  type PurchaseAllocationInput,
  type PurchaseGraph,
  type PurchaseHeaderInput,
  type PurchaseKind,
  type PurchaseLine,
  type PurchaseLineInput,
  type PurchaseStatus,
  type RefundMethod,
} from "../../domain/purchase";
import type { CostType } from "../../domain/proposedCostLine";
import type { FinancialBucket } from "../../domain/buckets";
import type { QuantityUnit } from "../../domain/feature";
import { computePurchase, lineOverAllocated } from "../../engine/purchaseMath";
import { ValidationError } from "./projectRepository";

interface PurchaseRow {
  id: string;
  kind: string;
  original_purchase_id: string | null;
  vendor: string;
  date: string;
  receipt_number: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  shipping_cents: number;
  total_cents: number;
  payment_method: string;
  refund_method: string | null;
  notes: string;
  status: string;
  voided_at: string | null;
  created_at: string;
  updated_at: string;
}

interface LineRow {
  id: string;
  purchase_id: string;
  original_line_id: string | null;
  description: string;
  quantity_x100: number | null;
  unit: string | null;
  sku: string;
  gross_cents: number;
  line_discount_cents: number;
  taxable: number;
  explicit_tax_cents: number | null;
  product_id: string | null;
  category_id: string | null;
  cost_type: string | null;
  notes: string;
  sort_order: number;
}

interface AllocationRow {
  id: string;
  line_id: string;
  project_id: string;
  amount_cents: number;
  quantity_x100: number | null;
  shipping_override_cents: number | null;
  tax_override_cents: number | null;
  category_override_id: string | null;
  bucket_override: string | null;
  linked_proposed_line_id: string | null;
  linked_feature_id: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

function mapPurchase(r: PurchaseRow): Purchase {
  return {
    id: r.id,
    kind: r.kind as PurchaseKind,
    originalPurchaseId: r.original_purchase_id,
    vendor: r.vendor,
    date: r.date,
    receiptNumber: r.receipt_number,
    subtotalCents: Number(r.subtotal_cents),
    discountCents: Number(r.discount_cents),
    taxCents: Number(r.tax_cents),
    shippingCents: Number(r.shipping_cents),
    totalCents: Number(r.total_cents),
    paymentMethod: r.payment_method,
    refundMethod: r.refund_method as RefundMethod | null,
    notes: r.notes,
    status: r.status as PurchaseStatus,
    voidedAt: r.voided_at,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

function mapLine(r: LineRow): PurchaseLine {
  return {
    id: r.id,
    purchaseId: r.purchase_id,
    originalLineId: r.original_line_id,
    description: r.description,
    quantityX100: r.quantity_x100 === null ? null : Number(r.quantity_x100),
    unit: r.unit as QuantityUnit | null,
    sku: r.sku,
    grossCents: Number(r.gross_cents),
    lineDiscountCents: Number(r.line_discount_cents),
    taxable: Number(r.taxable) === 1,
    explicitTaxCents: r.explicit_tax_cents === null ? null : Number(r.explicit_tax_cents),
    productId: r.product_id ?? null,
    categoryId: r.category_id,
    costType: r.cost_type as CostType | null,
    notes: r.notes,
    sortOrder: Number(r.sort_order),
  };
}

function mapAllocation(r: AllocationRow): PurchaseAllocation {
  return {
    id: r.id,
    lineId: r.line_id,
    projectId: r.project_id,
    amountCents: Number(r.amount_cents),
    quantityX100: r.quantity_x100 === null ? null : Number(r.quantity_x100),
    shippingOverrideCents:
      r.shipping_override_cents === null ? null : Number(r.shipping_override_cents),
    taxOverrideCents: r.tax_override_cents == null ? null : Number(r.tax_override_cents),
    categoryOverrideId: r.category_override_id,
    bucketOverride: r.bucket_override as FinancialBucket | null,
    linkedProposedLineId: r.linked_proposed_line_id,
    linkedFeatureId: r.linked_feature_id,
    notes: r.notes,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

const PURCHASE_SELECT = `SELECT * FROM purchases`;

export class PurchaseRepository {
  constructor(private readonly db: DbExecutor) {}

  private async assertCompanyMutable(purchaseId:string){if((await this.db.select("SELECT purchase_id FROM company_purchase_details WHERE purchase_id=?",[purchaseId])).length)throw new ValidationError(["Confirmed company source amounts and allocations are retained. Void and record a corrected source or add an explicit return instead."]);}

  /* ---------------- Reads ---------------- */

  async getGraph(purchaseId: string): Promise<PurchaseGraph | null> {
    const rows = await this.db.select<PurchaseRow>(`${PURCHASE_SELECT} WHERE id = ?`, [purchaseId]);
    if (rows.length === 0) return null;
    return this.hydrate(mapPurchase(rows[0]));
  }

  private async hydrate(purchase: Purchase): Promise<PurchaseGraph> {
    const lines = (
      await this.db.select<LineRow>(
        "SELECT * FROM purchase_lines WHERE purchase_id = ? ORDER BY sort_order",
        [purchase.id],
      )
    ).map(mapLine);
    const allocations =
      lines.length === 0
        ? []
        : (
            await this.db.select<AllocationRow>(
              `SELECT a.* FROM purchase_allocations a
                 JOIN purchase_lines l ON l.id = a.line_id
                WHERE l.purchase_id = ? ORDER BY a.created_at`,
              [purchase.id],
            )
          ).map(mapAllocation);
    return { purchase, lines, allocations };
  }

  /** All purchases (all projects), newest first. */
  async listAll(): Promise<PurchaseGraph[]> {
    const rows = await this.db.select<PurchaseRow>(
      `${PURCHASE_SELECT} ORDER BY date DESC, created_at DESC`,
    );
    const graphs: PurchaseGraph[] = [];
    for (const row of rows) graphs.push(await this.hydrate(mapPurchase(row)));
    return graphs;
  }

  /** Purchases with any allocation to the project (full graphs, since
   *  tax/shipping distribution needs the whole purchase). */
  async listTouchingProject(projectId: string): Promise<PurchaseGraph[]> {
    const rows = await this.db.select<PurchaseRow>(
      `SELECT DISTINCT p.* FROM purchases p
         JOIN purchase_lines l ON l.purchase_id = p.id
         JOIN purchase_allocations a ON a.line_id = l.id
        WHERE a.project_id = ?
        ORDER BY p.date DESC, p.created_at DESC`,
      [projectId],
    );
    const graphs: PurchaseGraph[] = [];
    for (const row of rows) graphs.push(await this.hydrate(mapPurchase(row)));
    return graphs;
  }

  /** Vendor names previously used (for typeahead). */
  async listVendors(): Promise<string[]> {
    const rows = await this.db.select<{ vendor: string }>(
      `SELECT DISTINCT vendor FROM purchases WHERE vendor <> ''
       UNION SELECT DISTINCT vendor FROM actual_expenses WHERE vendor <> ''
       ORDER BY vendor`,
    );
    return rows.map((r) => r.vendor);
  }

  async listReturnsOf(purchaseId: string): Promise<PurchaseGraph[]> {
    const rows = await this.db.select<PurchaseRow>(
      `${PURCHASE_SELECT} WHERE original_purchase_id = ? ORDER BY date`,
      [purchaseId],
    );
    const graphs: PurchaseGraph[] = [];
    for (const row of rows) graphs.push(await this.hydrate(mapPurchase(row)));
    return graphs;
  }

  /* ---------------- Header ---------------- */

  async createPurchase(input: PurchaseHeaderInput): Promise<Purchase> {
    const problems = validatePurchaseHeader(input);
    if (problems.length > 0) throw new ValidationError(problems);
    if (input.originalPurchaseId !== null) {
      const orig = await this.db.select<{ id: string; kind: string }>(
        "SELECT id, kind FROM purchases WHERE id = ?",
        [input.originalPurchaseId],
      );
      if (orig.length === 0) throw new ValidationError(["The original purchase no longer exists."]);
      if (orig[0].kind !== "purchase") throw new ValidationError(["A return must reference an original purchase, not another return."]);
    }
    const now = new Date().toISOString();
    const purchase: Purchase = {
      id: generateId(),
      kind: input.kind,
      originalPurchaseId: input.originalPurchaseId,
      vendor: input.vendor.trim(),
      date: input.date,
      receiptNumber: input.receiptNumber.trim(),
      subtotalCents: input.subtotalCents,
      discountCents: input.discountCents,
      taxCents: input.taxCents,
      shippingCents: input.shippingCents,
      totalCents: input.totalCents,
      paymentMethod: input.paymentMethod.trim(),
      refundMethod: input.kind === "return" ? input.refundMethod : null,
      notes: input.notes,
      status: "active",
      voidedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO purchases (id, kind, original_purchase_id, vendor, date, receipt_number,
         subtotal_cents, discount_cents, tax_cents, shipping_cents, total_cents,
         payment_method, refund_method, notes, status, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'active', ?, ?)`,
      [
        purchase.id, purchase.kind, purchase.originalPurchaseId, purchase.vendor, purchase.date,
        purchase.receiptNumber, purchase.subtotalCents, purchase.discountCents, purchase.taxCents,
        purchase.shippingCents, purchase.totalCents, purchase.paymentMethod, purchase.refundMethod,
        purchase.notes, purchase.createdAt, purchase.updatedAt,
      ],
    );
    return purchase;
  }

  /** Header edit. Never lets existing allocations exceed availability. */
  async updatePurchase(id: string, input: PurchaseHeaderInput): Promise<void> {
    await this.assertCompanyMutable(id);
    const problems = validatePurchaseHeader(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const graph = await this.getGraph(id);
    if (!graph) throw new Error(`Purchase ${id} not found.`);
    if (graph.purchase.kind !== input.kind) {
      throw new ValidationError(["A purchase's kind cannot be changed."]);
    }
    // Simulate: does the new discount strand any allocations?
    const simulated: PurchaseGraph = {
      ...graph,
      purchase: { ...graph.purchase, ...input, refundMethod: input.kind === "return" ? input.refundMethod : null },
    };
    this.assertNoOverAllocation(simulated);
    await this.db.execute(
      `UPDATE purchases SET vendor = ?, date = ?, receipt_number = ?, subtotal_cents = ?,
         discount_cents = ?, tax_cents = ?, shipping_cents = ?, total_cents = ?,
         payment_method = ?, refund_method = ?, notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.vendor.trim(), input.date, input.receiptNumber.trim(), input.subtotalCents,
        input.discountCents, input.taxCents, input.shippingCents, input.totalCents,
        input.paymentMethod.trim(), input.kind === "return" ? input.refundMethod : null,
        input.notes, new Date().toISOString(), id,
      ],
    );
  }

  private assertNoOverAllocation(graph: PurchaseGraph): void {
    const calc = computePurchase(graph);
    for (const line of calc.lines) {
      const explicit=graph.allocations.filter(a=>a.lineId===line.lineId&&a.taxOverrideCents!=null);
      if(explicit.some(a=>graph.purchase.kind==="purchase"?a.taxOverrideCents!<0:a.taxOverrideCents!>0)||Math.abs(explicit.reduce((s,a)=>s+a.taxOverrideCents!,0))>Math.abs(line.taxCents))throw new ValidationError(["Explicit allocation tax exceeds the original line tax or has the wrong sign."]);
      if (lineOverAllocated({ id: line.lineId }, line.netCents, line.allocatedCents)) {
        throw new ValidationError([
          `Line “${line.description}” would be over-allocated (${Math.abs(line.allocatedCents) / 100} allocated vs ${Math.abs(line.netCents) / 100} available). Reduce or reassign its allocations first.`,
        ]);
      }
    }
  }

  /* ---------------- Lines ---------------- */

  async addLine(purchaseId: string, input: PurchaseLineInput): Promise<PurchaseLine> {
    await this.assertCompanyMutable(purchaseId);
    const graph = await this.getGraph(purchaseId);
    if (!graph) throw new Error(`Purchase ${purchaseId} not found.`);
    const problems = validatePurchaseLine(graph.purchase.kind, input);
    if (problems.length > 0) throw new ValidationError(problems);
    if (input.originalLineId) {
      const orig = await this.db.select<{ purchase_id: string }>(
        "SELECT purchase_id FROM purchase_lines WHERE id = ?",
        [input.originalLineId],
      );
      if (orig.length === 0 || orig[0].purchase_id !== graph.purchase.originalPurchaseId) {
        throw new ValidationError(["A return line must reference a line of the original purchase."]);
      }
    }
    const maxSort = graph.lines.reduce((m, l) => Math.max(m, l.sortOrder), 0);
    const line: PurchaseLine = {
      id: generateId(),
      purchaseId,
      originalLineId: input.originalLineId ?? null,
      description: input.description.trim(),
      quantityX100: input.quantityX100,
      unit: input.unit,
      sku: input.sku.trim(),
      grossCents: input.grossCents,
      lineDiscountCents: input.lineDiscountCents,
      taxable: input.taxable,
      explicitTaxCents: input.explicitTaxCents,
      productId: input.productId ?? null,
      categoryId: input.categoryId,
      costType: input.costType,
      notes: input.notes,
      sortOrder: maxSort + 10,
    };
    await this.db.execute(
      `INSERT INTO purchase_lines (id, purchase_id, original_line_id, description, quantity_x100,
         unit, sku, gross_cents, line_discount_cents, taxable, explicit_tax_cents, product_id,
         category_id, cost_type, notes, sort_order)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        line.id, line.purchaseId, line.originalLineId, line.description, line.quantityX100,
        line.unit, line.sku, line.grossCents, line.lineDiscountCents, line.taxable ? 1 : 0,
        line.explicitTaxCents, line.productId, line.categoryId, line.costType, line.notes, line.sortOrder,
      ],
    );
    // A new line can change the discount distribution of others; verify.
    const after = await this.getGraph(purchaseId);
    if (after) this.assertNoOverAllocation(after);
    await this.touch(purchaseId);
    return line;
  }

  /** Line edit; blocked if it would strand existing allocations. */
  async updateLine(lineId: string, input: PurchaseLineInput): Promise<void> {
    const owner = await this.db.select<{ purchase_id: string }>(
      "SELECT purchase_id FROM purchase_lines WHERE id = ?",
      [lineId],
    );
    if (owner.length === 0) throw new Error(`Purchase line ${lineId} not found.`);
    await this.assertCompanyMutable(owner[0].purchase_id);
    const graph = await this.getGraph(owner[0].purchase_id);
    if (!graph) throw new Error("Purchase not found.");
    const problems = validatePurchaseLine(graph.purchase.kind, input);
    if (problems.length > 0) throw new ValidationError(problems);
    const simulated: PurchaseGraph = {
      ...graph,
      lines: graph.lines.map((l) =>
        l.id === lineId
          ? {
              ...l,
              description: input.description.trim(),
              quantityX100: input.quantityX100,
              unit: input.unit,
              sku: input.sku.trim(),
              grossCents: input.grossCents,
              lineDiscountCents: input.lineDiscountCents,
              taxable: input.taxable,
              explicitTaxCents: input.explicitTaxCents,
              categoryId: input.categoryId,
              costType: input.costType,
              notes: input.notes,
            }
          : l,
      ),
    };
    this.assertNoOverAllocation(simulated);
    await this.db.execute(
      `UPDATE purchase_lines SET description = ?, quantity_x100 = ?, unit = ?, sku = ?,
         gross_cents = ?, line_discount_cents = ?, taxable = ?, explicit_tax_cents = ?,
         category_id = ?, cost_type = ?, notes = ?
       WHERE id = ?`,
      [
        input.description.trim(), input.quantityX100, input.unit, input.sku.trim(),
        input.grossCents, input.lineDiscountCents, input.taxable ? 1 : 0, input.explicitTaxCents,
        input.categoryId, input.costType, input.notes, lineId,
      ],
    );
    await this.touch(graph.purchase.id);
  }

  /** Remove a line only when it has no allocations (financial history rule). */
  async removeLine(lineId: string): Promise<void> {
    const allocs = await this.db.select<{ c: number }>(
      "SELECT COUNT(*) AS c FROM purchase_allocations WHERE line_id = ?",
      [lineId],
    );
    if (Number(allocs[0].c) > 0) {
      throw new ValidationError(["Remove this line's allocations before removing the line."]);
    }
    const refs = await this.db.select<{ c: number }>(
      "SELECT COUNT(*) AS c FROM purchase_lines WHERE original_line_id = ?",
      [lineId],
    );
    if (Number(refs[0].c) > 0) {
      throw new ValidationError(["A return references this line; it cannot be removed."]);
    }
    const owner = await this.db.select<{ purchase_id: string }>(
      "SELECT purchase_id FROM purchase_lines WHERE id = ?",
      [lineId],
    );
    if(owner.length)await this.assertCompanyMutable(owner[0].purchase_id);
    await this.db.execute("DELETE FROM purchase_lines WHERE id = ?", [lineId]);
    if (owner.length > 0) await this.touch(owner[0].purchase_id);
  }

  /* ---------------- Allocations ---------------- */

  private async assertScopeLinks(input: PurchaseAllocationInput): Promise<void> {
    const projects = await this.db.select<{ id: string }>("SELECT id FROM projects WHERE id = ?", [
      input.projectId,
    ]);
    if (projects.length === 0) throw new ValidationError(["The allocation project no longer exists."]);
    if (input.linkedProposedLineId != null) {
      const rows = await this.db.select<{ project_id: string }>(
        "SELECT project_id FROM proposed_cost_lines WHERE id = ?",
        [input.linkedProposedLineId],
      );
      if (rows.length === 0) throw new ValidationError(["The linked proposed item no longer exists."]);
      if (rows[0].project_id !== input.projectId) {
        throw new ValidationError(["An allocation can only link to a proposed item in its own project."]);
      }
    }
    if (input.linkedFeatureId != null) {
      const rows = await this.db.select<{ project_id: string }>(
        "SELECT project_id FROM project_features WHERE id = ?",
        [input.linkedFeatureId],
      );
      if (rows.length === 0) throw new ValidationError(["The linked feature no longer exists."]);
      if (rows[0].project_id !== input.projectId) {
        throw new ValidationError(["An allocation can only link to a feature in its own project."]);
      }
    }
  }

  async addAllocation(lineId: string, input: PurchaseAllocationInput): Promise<PurchaseAllocation> {
    const problems = validateAllocation(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.assertScopeLinks(input);
    const owner = await this.db.select<{ purchase_id: string }>(
      "SELECT purchase_id FROM purchase_lines WHERE id = ?",
      [lineId],
    );
    if (owner.length === 0) throw new Error(`Purchase line ${lineId} not found.`);
    await this.assertCompanyMutable(owner[0].purchase_id);
    const graph = await this.getGraph(owner[0].purchase_id);
    if (!graph) throw new Error("Purchase not found.");
    if (graph.purchase.kind === "purchase" && input.amountCents < 0) {
      throw new ValidationError(["Allocations on a purchase cannot be negative."]);
    }
    if (graph.purchase.kind === "return" && input.amountCents > 0) {
      throw new ValidationError(["Return allocations must be entered as return (non-positive) amounts."]);
    }
    const now = new Date().toISOString();
    const allocation: PurchaseAllocation = {
      id: generateId(),
      lineId,
      projectId: input.projectId,
      amountCents: input.amountCents,
      quantityX100: input.quantityX100,
      shippingOverrideCents: input.shippingOverrideCents,
      taxOverrideCents: input.taxOverrideCents ?? null,
      categoryOverrideId: input.categoryOverrideId,
      bucketOverride: input.bucketOverride,
      linkedProposedLineId: input.linkedProposedLineId,
      linkedFeatureId: input.linkedFeatureId,
      notes: input.notes,
      createdAt: now,
      updatedAt: now,
    };
    // HARD RULE: allocated must never exceed available.
    this.assertNoOverAllocation({ ...graph, allocations: [...graph.allocations, allocation] });
    await this.db.execute(
      `INSERT INTO purchase_allocations (id, line_id, project_id, amount_cents, quantity_x100,
         shipping_override_cents, tax_override_cents, category_override_id, bucket_override, linked_proposed_line_id,
         linked_feature_id, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        allocation.id, allocation.lineId, allocation.projectId, allocation.amountCents,
        allocation.quantityX100, allocation.shippingOverrideCents, allocation.taxOverrideCents ?? null, allocation.categoryOverrideId,
        allocation.bucketOverride, allocation.linkedProposedLineId, allocation.linkedFeatureId,
        allocation.notes, allocation.createdAt, allocation.updatedAt,
      ],
    );
    await this.touch(graph.purchase.id);
    return allocation;
  }

  async updateAllocation(allocationId: string, input: PurchaseAllocationInput): Promise<void> {
    const problems = validateAllocation(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.assertScopeLinks(input);
    const owner = await this.db.select<{ purchase_id: string }>(
      `SELECT l.purchase_id FROM purchase_allocations a JOIN purchase_lines l ON l.id = a.line_id
        WHERE a.id = ?`,
      [allocationId],
    );
    if (owner.length === 0) throw new Error(`Allocation ${allocationId} not found.`);
    await this.assertCompanyMutable(owner[0].purchase_id);
    const graph = await this.getGraph(owner[0].purchase_id);
    if (!graph) throw new Error("Purchase not found.");
    const simulated: PurchaseGraph = {
      ...graph,
      allocations: graph.allocations.map((a) => (a.id === allocationId ? { ...a, ...input } : a)),
    };
    this.assertNoOverAllocation(simulated);
    await this.db.execute(
      `UPDATE purchase_allocations SET project_id = ?, amount_cents = ?, quantity_x100 = ?,
         shipping_override_cents = ?, tax_override_cents = ?, category_override_id = ?, bucket_override = ?,
         linked_proposed_line_id = ?, linked_feature_id = ?, notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.projectId, input.amountCents, input.quantityX100, input.shippingOverrideCents, input.taxOverrideCents ?? null,
        input.categoryOverrideId, input.bucketOverride, input.linkedProposedLineId,
        input.linkedFeatureId, input.notes, new Date().toISOString(), allocationId,
      ],
    );
    await this.touch(graph.purchase.id);
  }

  async removeAllocation(allocationId: string): Promise<void> {
    const owner = await this.db.select<{ purchase_id: string }>(
      `SELECT l.purchase_id FROM purchase_allocations a JOIN purchase_lines l ON l.id = a.line_id
        WHERE a.id = ?`,
      [allocationId],
    );
    if(owner.length)await this.assertCompanyMutable(owner[0].purchase_id);
    await this.db.execute("DELETE FROM purchase_allocations WHERE id = ?", [allocationId]);
    if (owner.length > 0) await this.touch(owner[0].purchase_id);
  }

  /* ---------------- Lifecycle ---------------- */

  private async touch(purchaseId: string): Promise<void> {
    await this.db.execute("UPDATE purchases SET updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      purchaseId,
    ]);
  }

  /** Void: excluded from every total, retained for history. */
  async voidPurchase(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE purchases SET status = 'voided', voided_at = ?, updated_at = ? WHERE id = ? AND status = 'active'",
      [now, now, id],
    );
  }

  async unvoidPurchase(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE purchases SET status = 'active', voided_at = NULL, updated_at = ? WHERE id = ?",
      [new Date().toISOString(), id],
    );
  }

  /** True when the purchase has no allocations, attachments, or returns. */
  async isDeletable(id: string): Promise<boolean> {
    const allocs = await this.db.select<{ c: number }>(
      `SELECT COUNT(*) AS c FROM purchase_allocations a JOIN purchase_lines l ON l.id = a.line_id
        WHERE l.purchase_id = ?`,
      [id],
    );
    const attachments = await this.db.select<{ c: number }>(
      "SELECT COUNT(*) AS c FROM purchase_attachments WHERE purchase_id = ?",
      [id],
    );
    const returns = await this.db.select<{ c: number }>(
      "SELECT COUNT(*) AS c FROM purchases WHERE original_purchase_id = ?",
      [id],
    );
    return Number(allocs[0].c) === 0 && Number(attachments[0].c) === 0 && Number(returns[0].c) === 0;
  }

  /** Physical delete ONLY for dependency-free mistakes; otherwise void. */
  async deleteIfUnreferenced(id: string): Promise<void> {
    await this.assertCompanyMutable(id);
    if (!(await this.isDeletable(id))) {
      throw new ValidationError([
        "This purchase has allocations, attachments, or returns. Void it instead of deleting.",
      ]);
    }
    await this.db.execute("DELETE FROM purchase_lines WHERE purchase_id = ?", [id]);
    await this.db.execute("DELETE FROM purchases WHERE id = ?", [id]);
  }

  /* ---------------- Conveniences ---------------- */

  /**
   * Quick Receipt: one purchase → one catch-all line → one allocation to a
   * project. Same architecture, simplified front end.
   */
  async createQuickReceipt(input: {
    vendor: string;
    date: string;
    receiptNumber: string;
    subtotalCents: number;
    taxCents: number;
    totalCents: number;
    description: string;
    projectId: string;
    categoryId: string | null;
    costType: CostType | null;
    bucketOverride: FinancialBucket | null;
    linkedProposedLineId: string | null;
    linkedFeatureId: string | null;
    notes: string;
  }): Promise<PurchaseGraph> {
    const purchase = await this.createPurchase({
      kind: "purchase",
      originalPurchaseId: null,
      vendor: input.vendor,
      date: input.date,
      receiptNumber: input.receiptNumber,
      subtotalCents: input.subtotalCents,
      discountCents: 0,
      taxCents: input.taxCents,
      shippingCents: 0,
      totalCents: input.totalCents,
      paymentMethod: "",
      refundMethod: null,
      notes: input.notes,
    });
    const line = await this.addLine(purchase.id, {
      description: input.description,
      quantityX100: null,
      unit: null,
      sku: "",
      grossCents: input.subtotalCents,
      lineDiscountCents: 0,
      taxable: input.taxCents !== 0,
      explicitTaxCents: null,
      categoryId: input.categoryId,
      costType: input.costType,
      notes: "",
    });
    await this.addAllocation(line.id, {
      projectId: input.projectId,
      amountCents: input.subtotalCents,
      quantityX100: null,
      shippingOverrideCents: null,
      categoryOverrideId: null,
      bucketOverride: input.bucketOverride,
      linkedProposedLineId: input.linkedProposedLineId,
      linkedFeatureId: input.linkedFeatureId,
      notes: "",
    });
    const graph = await this.getGraph(purchase.id);
    if (!graph) throw new Error("Quick receipt vanished.");
    return graph;
  }

  /**
   * Create a return against an original purchase: negative header, and for
   * each requested original line a negative return line + allocations
   * mirroring the original allocations proportionally (user may edit after).
   */
  async createReturn(input: {
    originalPurchaseId: string;
    date: string;
    receiptNumber: string;
    refundMethod: RefundMethod;
    notes: string;
    /** Positive magnitudes to return per original line (pre-tax net). */
    lines: Array<{ originalLineId: string; returnNetCents: number; description?: string }>;
    /** Positive tax refunded (receipt figure). */
    taxRefundCents: number;
  }): Promise<PurchaseGraph> {
    await this.assertCompanyMutable(input.originalPurchaseId);
    const original = await this.getGraph(input.originalPurchaseId);
    if (!original) throw new ValidationError(["The original purchase no longer exists."]);
    if (original.purchase.kind !== "purchase") {
      throw new ValidationError(["Returns can only be created against an original purchase."]);
    }
    if (input.lines.length === 0) throw new ValidationError(["Choose at least one line to return."]);
    for (const l of input.lines) {
      if (!Number.isSafeInteger(l.returnNetCents) || l.returnNetCents <= 0) {
        throw new ValidationError(["Return amounts must be positive."]);
      }
    }
    if (!Number.isSafeInteger(input.taxRefundCents) || input.taxRefundCents < 0) {
      throw new ValidationError(["Tax refund must be a valid non-negative amount."]);
    }
    const subtotal = -input.lines.reduce((s, l) => s + l.returnNetCents, 0);
    const tax = -input.taxRefundCents;
    const purchase = await this.createPurchase({
      kind: "return",
      originalPurchaseId: input.originalPurchaseId,
      vendor: original.purchase.vendor,
      date: input.date,
      receiptNumber: input.receiptNumber,
      subtotalCents: subtotal,
      discountCents: 0,
      taxCents: tax,
      shippingCents: 0,
      totalCents: subtotal + tax,
      paymentMethod: "",
      refundMethod: input.refundMethod,
      notes: input.notes,
    });
    for (const l of input.lines) {
      const origLine = original.lines.find((x) => x.id === l.originalLineId);
      if (!origLine) throw new ValidationError(["A return line references an unknown original line."]);
      const line = await this.addLine(purchase.id, {
        originalLineId: origLine.id,
        productId: origLine.productId,
        description: l.description ?? `Return: ${origLine.description}`,
        quantityX100: null,
        unit: origLine.unit,
        sku: origLine.sku,
        grossCents: -l.returnNetCents,
        lineDiscountCents: 0,
        taxable: origLine.taxable,
        explicitTaxCents: null,
        categoryId: origLine.categoryId,
        costType: origLine.costType,
        notes: "",
      });
      // Mirror original allocations proportionally (largest-remainder).
      const origAllocs = original.allocations.filter((a) => a.lineId === origLine.id);
      if (origAllocs.length > 0) {
        const { distributeProportionally } = await import("../../engine/purchaseMath");
        const shares = distributeProportionally(
          -l.returnNetCents,
          origAllocs.map((a) => a.amountCents),
        );
        for (let i = 0; i < origAllocs.length; i++) {
          if (shares[i] === 0) continue;
          const a = origAllocs[i];
          await this.addAllocation(line.id, {
            projectId: a.projectId,
            amountCents: shares[i],
            quantityX100: null,
            shippingOverrideCents: null,
            categoryOverrideId: a.categoryOverrideId,
            bucketOverride: a.bucketOverride,
            linkedProposedLineId: a.linkedProposedLineId,
            linkedFeatureId: a.linkedFeatureId,
            notes: "Mirrors original allocation",
          });
        }
      }
    }
    const graph = await this.getGraph(purchase.id);
    if (!graph) throw new Error("Return vanished.");
    return graph;
  }
}
